import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Crown, Eye, EyeOff, Heart, ImagePlus, Lock, MessageCircle, Plus, Send, Star, Trophy, X } from "lucide-react";
import { EASE, HEART, Head, Ink, VIEW } from "../bits";
import { Part } from "../Page";
import { CHALLENGE, CHELSEA, FOUNDING, MAKES, MEMBERS, PLANS, PROJECTS, can, countComments, viewer, type Comment, type Make, type Member, type Role } from "./sample";
import { JoinButton, Locked, M, MembersPage, joinCta, useRole, withRole } from "./Members";
import { Avatar, useDialog, useToast } from "./Account";
import "../SectionsA.css";
import "./Community.css";

/* Makes (/blue-2/members/community): the club's show-and-tell. The monthly
   challenge up top, then a masonry wall of taped polaroids that members can
   filter, heart and comment on, and a "Share your make" form that adds a make
   locally (with a real photo preview via an object URL). Thread Friends can
   browse and heart; visitors get a blurred peek behind the club's shared
   Locked card. Chelsea can feature a make,
   crown the challenge winner and hide makes. All state is local. */

/* ─── Sample data (extends sample.ts MAKES) ─── */

const [sam, jo, lee, mara, dana, priya] = MEMBERS;

type CMake = Make & {
  aspect: string; pos?: string; zoom?: number; // how the photo is cropped on the card
  at: number; ago: string; story?: string; comments: Comment[];
  hidden?: boolean; mine?: boolean; swatch?: number;
};

const c = (id: string, by: Member, text: string, ago: string, likes = 0, replies?: Comment[]): Comment => ({ id, by, text, ago, likes, replies });

/** Card shapes and stories for the shared sample makes. */
const EXTRA: Record<string, Omit<CMake, keyof Make>> = {
  k1: { aspect: "4/3", at: 2880, ago: "2 days ago", story: "Cut from a thrifted gingham quilt. Binding took me two evenings but I am SO proud of it.",
    comments: [c("k1a", CHELSEA, "Look at those matched checks at the side seams! Wear it everywhere.", "2d", 12), c("k1b", mara, "Okay this is going on my list.", "1d", 3)] },
  k2: { aspect: "4/5", at: 4320, ago: "3 days ago", story: "Three pouches, three sisters, zero zipper tears. Thank you for the zipper class!",
    comments: [c("k2a", priya, "Your sisters are lucky.", "3d", 4)] },
  k3: { aspect: "1/1", at: 6000, ago: "4 days ago", story: "Ordered custom labels and now I want to sew everything just to use them.",
    comments: [c("k3a", CHELSEA, "Good Outfits Brighter Days is the best motto.", "4d", 9)] },
  k4: { aspect: "3/4", pos: "50% 35%", at: 30000, ago: "3 weeks ago", story: "Every scrap I own, sorted by color. September’s challenge broke my brain in the best way.",
    comments: [c("k4a", CHELSEA, "September winner! The rainbow sort is a whole mood.", "3w", 21, [c("k4r", priya, "I’m framing this comment.", "3w", 8)])] },
  k5: { aspect: "4/5", pos: "55% 46%", zoom: 3, at: 900, ago: "15 min ago", story: "Front panel done after class one. Pressing tip at 58:02 saved me.",
    comments: [c("k5a", sam, "That pressing tip!!", "10m", 2)] },
  k6: { aspect: "4/5", pos: "45% 55%", at: 7200, ago: "5 days ago", story: "My studio helper insists on testing every quilt for nap quality.",
    comments: [c("k6a", jo, "Quality control department looks very serious.", "5d", 6)] },
};

const OCT = CHALLENGE.title;
const MORE: CMake[] = [
  { id: "x1", by: jo, title: "Free-motion scrap panel", image: "/img/karleeporter.jpg", aspect: "1/1", pos: "80.7% 59%", zoom: 3, projectId: "tote", challenge: OCT, likes: 12, at: 300, ago: "5 min ago",
    story: "Every color from the scrap bin, then I went wild with free-motion quilting. Tote panel number one!", comments: [c("x1a", lee, "That swirly quilting!!", "2m", 1)] },
  { id: "x2", by: mara, title: "Embroidered round bag", image: "/chelsea.JPG", aspect: "4/5", pos: "34% 74%", zoom: 2.1, challenge: OCT, likes: 52, at: 1500, ago: "25 min ago",
    story: "Scrap-busting bonus round: a round bag from my smallest scraps, plus some very slow embroidery.",
    comments: [c("x2a", CHELSEA, "I need this bag in my life. The flower!!", "20m", 15), c("x2b", dana, "How long did the embroidery take?", "18m", 2, [c("x2r", mara, "About four episodes of a cozy show.", "15m", 5)])] },
  { id: "x3", by: dana, title: "Plaid wall quilt, finally hung", image: "/video/live-class.jpg", aspect: "4/3", pos: "56% 4%", zoom: 1.7, likes: 23, at: 10000, ago: "1 week ago",
    story: "Started it in a club class last spring. It lives on the studio wall now.", comments: [] },
  { id: "x4", by: priya, title: "Scrappy tote panel #1", image: "/chelsea-and-cosmo.jpg", aspect: "4/3", pos: "45.7% 90.7%", zoom: 3.3, projectId: "tote", challenge: OCT, likes: 19, at: 2000, ago: "40 min ago",
    story: "Improv piecing, no ruler, no regrets.", comments: [c("x4a", sam, "The colors!!", "30m", 3)] },
  { id: "x5", by: sam, title: "Baby quilt for my niece, ready to bind", image: "/video/live-class.jpg", aspect: "4/3", pos: "100% 100%", zoom: 2.5, likes: 9, at: 15000, ago: "1 week ago",
    story: "Minky backing is slippery and I have the walking-foot bruises to prove it. Worth it.", comments: [c("x5a", CHELSEA, "Minky is a boss level fabric. Look at you!", "6d", 7)] },
];

const START: CMake[] = [...MAKES.map(m => ({ ...m, ...EXTRA[m.id] })), ...MORE];
const LAST_WINNER = "k4";
const projectTitle = (id?: string) => PROJECTS.find(p => p.id === id)?.title;
const TAPES = ["soft", "extra", "pop", "strong"] as const;
const TILT = [-2.2, 1.6, -1.1, 2.3, -1.6, 1.1];

/** Days until the challenge closes (end of its last day, Mountain time). Parsed
    by hand because Safari won't parse "Oct 31, 2026 23:59:59 GMT-0600". */
function daysLeft() {
  const [mon, day] = CHALLENGE.ends.split(" ");
  const month = "JanFebMarAprMayJunJulAugSepOctNovDec".indexOf(mon) / 3;
  const end = Date.UTC(new Date().getFullYear(), month, Number(day) + 1, 6) - 1000; // 23:59:59 MDT = 05:59:59 UTC next day
  return Math.ceil((end - Date.now()) / 864e5);
}

/* ─── Page state ─── */

type Filter = "all" | "challenge" | "other" | string; // or a project id
type Sort = "new" | "loved";

function useCommunityState() {
  const [makes, setMakes] = useState<CMake[]>(START);
  const [liked, setLiked] = useState<Set<string>>(() => new Set(["x2", "k1"]));
  const [featured, setFeatured] = useState("x2");
  const [winner, setWinner] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [sharing, setSharing] = useState<false | { challenge: boolean }>(false);
  const [upsell, setUpsell] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("new");
  const [fresh, setFresh] = useState<string | null>(null);
  const toast = useToast();

  // Object URLs for uploaded photos live as long as the page does.
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach(u => URL.revokeObjectURL(u)), []);

  const like = (id: string) => {
    const was = liked.has(id);
    setLiked(s => { const n = new Set(s); if (was) n.delete(id); else n.add(id); return n; });
    setMakes(ms => ms.map(m => m.id === id ? { ...m, likes: m.likes + (was ? -1 : 1) } : m));
  };
  const comment = (id: string, by: Member, text: string) =>
    setMakes(ms => ms.map(m => m.id === id ? { ...m, comments: [...m.comments, c(`u${Date.now()}`, by, text, "now")] } : m));
  const hide = (id: string) => {
    const m = makes.find(x => x.id === id)!;
    setMakes(ms => ms.map(x => x.id === id ? { ...x, hidden: !x.hidden } : x));
    toast.show(m.hidden ? `“${m.title}” is visible again.` : `Hidden from the club. Only you can see “${m.title}”.`);
  };
  const feature = (id: string) => {
    setFeatured(id);
    toast.show(`Featured! “${makes.find(m => m.id === id)!.title}” is up top.`);
  };
  const crown = (id: string) => {
    setWinner(id); setPicking(false);
    const m = makes.find(x => x.id === id)!;
    toast.show(`Crowned! ${m.by.name} wins ${OCT}.`);
  };
  const add = (m: CMake, url?: string) => {
    if (url) urls.current.push(url);
    setMakes(ms => [m, ...ms]);
    setFilter("all"); setSort("new"); setFresh(m.id);
    toast.show(m.challenge ? "Posted and entered! Good luck." : "Posted! Your make is at the top of the wall.");
  };
  const entries = CHALLENGE.entries + makes.filter(m => m.mine && m.challenge === OCT).length;
  return { makes, liked, like, comment, hide, feature, featured, winner, setWinner, crown, picking, setPicking, open, setOpen,
    sharing, setSharing, upsell, setUpsell, filter, setFilter, sort, setSort, add, entries, fresh, toast };
}
type State = ReturnType<typeof useCommunityState>;
const Ctx = createContext<State | null>(null);
const useC = () => useContext(Ctx)!;

/** Makes this role may see: hidden ones only show to Chelsea. */
const visibleTo = (makes: CMake[], role: Role) => role === "chelsea" ? makes : makes.filter(m => !m.hidden);
const roleLink = (to: string, role: Role) => withRole(to, role);
/** Who's posting or commenting: the shared sample member (ME), or Chelsea. Only called for signed-in roles. */
const me = (role: Role): Member => viewer(role)!;

/** "Share your make" for every role: members open the form, Thread Friends see why not, visitors go join. */
function ShareButton({ className, challenge = false, children }: { className: string; challenge?: boolean; children?: ReactNode }) {
  const role = useRole();
  const s = useC();
  const label = children ?? <><Plus size={18} aria-hidden="true" /> Share your make</>;
  const join = joinCta(role, "members");
  if (role === "visitor" && join) return <Link to={join.to} className={className}>{join.short} <ArrowRight size={16} aria-hidden="true" /></Link>;
  if (role === "friend") return <button type="button" className={className} onClick={() => s.setUpsell(true)}><Lock size={16} aria-hidden="true" /> {challenge ? "Enter the challenge" : "Share your make"}</button>;
  return <button type="button" className={className} onClick={() => s.setSharing({ challenge })}>{label}</button>;
}

/* ─── Photo on a card ─── */

function Photo({ m, sizes = "card" }: { m: CMake; sizes?: "card" | "big" }) {
  if (!m.image) return <span className={`mm-swatch mm-swatch-${m.swatch ?? 0}`} aria-hidden="true"><b>{m.title.slice(0, 1)}</b></span>;
  const style = { objectPosition: m.pos, transformOrigin: m.pos, scale: m.zoom ? String(m.zoom) : undefined };
  return <img src={m.image} alt={sizes === "big" ? `${m.title}, made by ${m.by.name}` : ""} style={style} loading={sizes === "card" ? "lazy" : undefined} draggable={false} />;
}

/* ─── Header ─── */

function Header() {
  const role = useRole();
  const s = useC();
  const shown = visibleTo(s.makes, role);
  const pick = shown.find(m => m.id === s.featured) ?? shown[0];
  const locked = role === "visitor";
  return <div className="mm-head">
    <div className="mm-head-copy">
      <motion.p className="mm-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · Makes</motion.p>
      <h1 className="mm-title">
        <motion.span className="mm-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.35, duration: 0.5, ease: EASE }}>look what we</motion.span>
        <span className="mm-title-word" aria-label="made">
          {"made".split("").map((ch, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{ch}</motion.span>)}
        </span>
      </h1>
      <motion.p className="mm-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.45, ease: EASE }}>
        The club’s show-and-tell. Post what’s on your sewing table, cheer everyone else on, and enter this month’s challenge.
      </motion.p>
      <motion.div className="mm-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.45, ease: EASE }}>
        <ShareButton className="mb-btn mb-btn-hero" />
        <a href="#mm-challenge" className="mb-btn mb-btn-line">This month’s challenge</a>
      </motion.div>
      <motion.ul className="mm-stats" aria-label="Makes so far" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
        <li><b>{locked ? "180+" : shown.length + 172}</b> makes shared</li>
        <li><b aria-live="polite">{s.entries}</b> challenge entries</li>
      </motion.ul>
    </div>
    {pick && <motion.figure className="mm-pick" initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 15 }}>
      <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
      {locked
        ? <span className="mm-pick-img"><Photo m={pick} sizes="big" /></span>
        : <button type="button" className="mm-pick-img" onClick={() => s.setOpen(pick.id)} aria-label={`Open Chelsea’s pick: ${pick.title} by ${pick.by.name}`}><Photo m={pick} /></button>}
      <figcaption className="mm-pick-cap"><span>{pick.title}</span><small>by {pick.by.name}</small></figcaption>
      <span className="mm-pick-sticker" aria-hidden="true">Chelsea’s<br />pick!</span>
      <svg className="mm-pick-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={1.1} width={3.2} /></svg>
    </motion.figure>}
  </div>;
}

/* ─── Challenge ─── */

function Challenge() {
  const role = useRole();
  const s = useC();
  const left = daysLeft();
  const winner = s.winner ? s.makes.find(m => m.id === s.winner) : undefined;
  const last = s.makes.find(m => m.id === LAST_WINNER)!;
  const chelsea = role === "chelsea";
  const startPicking = () => {
    s.setPicking(true); s.setFilter("challenge");
    document.getElementById("mm-wall")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const show = winner ?? last;
  return <section id="mm-challenge" className="b2-sec mm-chal" aria-labelledby="mm-chal-title">
    <div className="b2-wrap mm-chal-grid">
      <motion.div className="mm-chal-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
        <svg className="mm-rosette" viewBox="0 0 120 150" aria-hidden="true">
          <path className="mm-rosette-tail" d="M44 70 L30 146 L46 132 L58 148 L62 78 Z M76 70 L90 146 L74 132 L62 148 L58 78 Z" />
          <circle className="mm-rosette-out" cx="60" cy="56" r="50" />
          <circle className="mm-rosette-in" cx="60" cy="56" r="36" />
          <text x="60" y="52" textAnchor="middle">win a</text><text x="60" y="70" textAnchor="middle">FQ bundle</text>
        </svg>
        <p className="mm-chal-kicker"><Trophy size={15} aria-hidden="true" /> This month’s challenge</p>
        <h2 id="mm-chal-title" className="mm-chal-title">{CHALLENGE.title}</h2>
        <p className="mm-chal-blurb">{CHALLENGE.blurb}</p>
        <dl className="mm-chal-stats">
          <div><dt>{left > 0 ? (left === 1 ? "day left" : "days left") : "closed"}</dt><dd>{left > 0 ? left : "✓"}</dd></div>
          <div><dt>entries</dt><dd aria-live="polite">{s.entries}</dd></div>
          <div><dt>deadline</dt><dd>{CHALLENGE.ends}</dd></div>
        </dl>
        <div className="mm-chal-actions">
          {chelsea
            ? <><button type="button" className="mb-btn mb-btn-pop" onClick={startPicking} aria-pressed={s.picking}><Crown size={17} aria-hidden="true" /> {winner ? "Pick a different winner" : "Pick a winner"}</button>
                <ShareButton className="mb-btn mb-btn-line" challenge><Plus size={17} aria-hidden="true" /> Post an example</ShareButton></>
            : <><ShareButton className="mb-btn mb-btn-pop" challenge><Plus size={17} aria-hidden="true" /> Enter the challenge</ShareButton>
                {role !== "visitor" && <button type="button" className="mb-btn mb-btn-line" onClick={() => { s.setFilter("challenge"); document.getElementById("mm-wall")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>See the entries</button>}</>}
        </div>
        {role === "friend" && <p className="mb-lockrow mm-chal-note"><Lock size={14} aria-hidden="true" /> <span>Entering is for Members. You can still heart every entry!</span>
          <Link to={roleLink(M.join, role)}>{joinCta(role)?.short} <ArrowRight size={13} aria-hidden="true" /></Link></p>}
      </motion.div>

      <motion.div className="mm-chal-side" initial={{ opacity: 0, y: 40, rotate: 6 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={VIEW} transition={{ delay: 0.15, type: "spring", stiffness: 120, damping: 15 }}>
        <p className="b2-note mm-chal-side-note">{winner ? "and the winner is…" : "last month’s winner!"}</p>
        <figure className="mm-winner">
          <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
          {role === "visitor"
            ? <span className="mm-winner-img"><Photo m={show} sizes="big" /></span>
            : <button type="button" className="mm-winner-img" onClick={() => s.setOpen(show.id)} aria-label={`Open ${show.title} by ${show.by.name}`}><Photo m={show} /></button>}
          <figcaption><b><Crown size={16} aria-hidden="true" /> {show.by.name}</b><span>{show.title} · {winner ? OCT : show.challenge}</span></figcaption>
        </figure>
        {chelsea && winner && <button type="button" className="mm-undo" onClick={() => s.setWinner(null)}>Undo pick</button>}
      </motion.div>
    </div>
  </section>;
}

/* ─── The wall ─── */

const ROW = 4; // grid-auto-rows in px; each tile spans enough rows for its height

/** One grid cell that measures its card so the grid packs like masonry while keeping reading order. */
function Tile({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLLIElement>(null);
  const [span, setSpan] = useState(80);
  useLayoutEffect(() => {
    const card = ref.current?.firstElementChild as HTMLElement | null;
    if (!card) return;
    const gap = () => parseFloat(getComputedStyle(ref.current!).getPropertyValue("--mm-gap")) || 40;
    const ro = new ResizeObserver(() => setSpan(Math.ceil((card.offsetHeight + gap()) / ROW)));
    ro.observe(card);
    return () => ro.disconnect();
  }, []);
  return <li ref={ref} className={`mm-tile ${className}`} style={{ gridRowEnd: `span ${span}` }}>{children}</li>;
}

function LikeButton({ m, big = false }: { m: CMake; big?: boolean }) {
  const s = useC();
  const on = s.liked.has(m.id);
  const reduce = useReducedMotion();
  return <button type="button" className={`mm-like${on ? " is-on" : ""}${big ? " mm-like-big" : ""}`} aria-pressed={on}
    aria-label={`${on ? "Unheart" : "Heart"} “${m.title}”, ${m.likes} ${m.likes === 1 ? "heart" : "hearts"}`} onClick={() => s.like(m.id)}>
    <motion.span key={on ? "on" : "off"} className="mm-like-icon" initial={reduce || !on ? false : { scale: 0.4 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 14 }}>
      <Heart size={big ? 20 : 17} fill={on ? "currentColor" : "none"} aria-hidden="true" />
    </motion.span>
    <span aria-hidden="true">{m.likes}</span>
  </button>;
}

function Tools({ m, labels = false }: { m: CMake; labels?: boolean }) {
  const s = useC();
  const entry = m.challenge === OCT;
  const isPick = s.featured === m.id, isWinner = s.winner === m.id;
  return <div className={`mm-tools${labels ? " mm-tools-labels" : ""}`} role="group" aria-label={`Creator tools for “${m.title}”`}>
    <button type="button" aria-pressed={isPick} onClick={() => s.feature(m.id)} disabled={isPick || m.hidden} title="Feature as Chelsea’s pick">
      <Star size={16} fill={isPick ? "currentColor" : "none"} aria-hidden="true" /><span className={labels ? "" : "ma-sr"}>{isPick ? "Featured" : "Feature"}</span></button>
    {entry && <button type="button" aria-pressed={isWinner} onClick={() => s.crown(m.id)} disabled={isWinner || m.hidden} title="Crown challenge winner">
      <Crown size={16} aria-hidden="true" /><span className={labels ? "" : "ma-sr"}>{isWinner ? "Winner" : "Crown winner"}</span></button>}
    <button type="button" aria-pressed={!!m.hidden} onClick={() => s.hide(m.id)} title={m.hidden ? "Show to the club again" : "Hide from the club"}>
      {m.hidden ? <Eye size={16} aria-hidden="true" /> : <EyeOff size={16} aria-hidden="true" />}<span className={labels ? "" : "ma-sr"}>{m.hidden ? "Unhide" : "Hide"}</span></button>
  </div>;
}

function Card({ m, i }: { m: CMake; i: number }) {
  const role = useRole();
  const s = useC();
  const reduce = useReducedMotion();
  const tilt = TILT[i % TILT.length];
  const project = projectTitle(m.projectId);
  const entry = m.challenge === OCT;
  const comments = countComments(m, role === "chelsea");
  const ref = useRef<HTMLElement>(null);
  useEffect(() => { if (s.fresh === m.id) ref.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" }); }, [s.fresh, m.id, reduce]);
  return <motion.article ref={ref} className={`mm-make${m.hidden ? " is-hidden" : ""}${s.picking && entry ? " is-pickable" : ""}`}
    initial={reduce ? false : { opacity: 0, y: 36, rotate: tilt + 4 }} whileInView={{ opacity: 1, y: 0, rotate: tilt }} viewport={VIEW}
    transition={{ delay: (i % 4) * 0.06, type: "spring", stiffness: 140, damping: 16 }} aria-labelledby={`mm-t-${m.id}`}>
    <i className={`b2-snap-tape b2-ktape-${TAPES[i % TAPES.length]}`} aria-hidden="true" />
    <button type="button" className="mm-make-photo" style={{ aspectRatio: m.aspect }} onClick={() => s.setOpen(m.id)} aria-label={`Open “${m.title}” by ${m.by.name}`}>
      <Photo m={m} />
      {s.featured === m.id && <em className="mm-flag mm-flag-pick"><Star size={12} fill="currentColor" aria-hidden="true" /> Chelsea’s pick</em>}
      {s.winner === m.id && <em className="mm-flag mm-flag-win"><Crown size={13} aria-hidden="true" /> Winner!</em>}
      {m.id === LAST_WINNER && <em className="mm-flag mm-flag-win"><Crown size={13} aria-hidden="true" /> Sept. winner</em>}
      {s.fresh === m.id && <em className="mm-new" aria-hidden="true">new!</em>}
      {m.hidden && <span className="mm-hidden-veil"><EyeOff size={18} aria-hidden="true" /> Hidden from the club</span>}
    </button>
    <div className="mm-make-body">
      <h3 id={`mm-t-${m.id}`} className="mm-make-title">{m.title}</h3>
      <p className="mm-make-by"><Avatar m={m.by} size={24} /> <span>{m.by.name}</span>{m.by.badge === "founding" && <span className="mm-founding">{FOUNDING}</span>}</p>
      {(project || entry || m.challenge) && <p className="mm-tags">
        {project && <span className="mm-tag">{project}</span>}
        {m.challenge && <span className={`mm-tag mm-tag-chal${entry ? "" : " is-past"}`}><Trophy size={11} aria-hidden="true" /> {entry ? "Challenge" : m.challenge}</span>}
      </p>}
      <div className="mm-make-foot">
        <LikeButton m={m} />
        <button type="button" className="mm-comments" onClick={() => s.setOpen(m.id)} aria-label={`${comments} ${comments === 1 ? "comment" : "comments"} on “${m.title}”`}>
          <MessageCircle size={16} aria-hidden="true" /> <span aria-hidden="true">{comments}</span>
        </button>
        <span className="mm-ago">{m.ago}</span>
      </div>
      {role === "chelsea" && <Tools m={m} />}
    </div>
    {s.picking && entry && <button type="button" className="mm-crown-btn" onClick={() => s.crown(m.id)}><Crown size={18} aria-hidden="true" /> Crown this one</button>}
  </motion.article>;
}

function Wall() {
  const role = useRole();
  const s = useC();
  const shown = visibleTo(s.makes, role);
  const count = (f: Filter) => shown.filter(m => matches(m, f)).length;
  const list = arrange(shown.filter(m => matches(m, s.filter)), s.sort);
  const hiddenCount = s.makes.filter(m => m.hidden).length;
  const FILTERS: { f: Filter; label: ReactNode }[] = [
    { f: "all", label: "All makes" },
    { f: "challenge", label: <><Trophy size={13} aria-hidden="true" /> October challenge</> },
    ...PROJECTS.map(p => ({ f: p.id, label: p.title })),
    { f: "other", label: "Everything else" },
  ];
  return <section id="mm-wall" className="b2-sec mm-wall" aria-labelledby="mm-wall-title">
    <div className="b2-wrap">
      <div className="b2-row mm-wall-row">
        <Head id="mm-wall-title" hand="show &" word="tell" tone="pink" className="mb-headsm" />
        <div className="b2-row-aside mm-wall-aside">
          <p className="b2-note">heart your faves!</p>
          <svg className="b2-arrow" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M70 6 C 56 30, 36 44, 12 48" delay={0.5} /><Ink d="M26 36 L11 48 L26 58" delay={0.9} /></svg>
        </div>
      </div>

      <div className="mm-controls">
        <div className="mm-chips" role="group" aria-label="Filter makes">
          {FILTERS.map(({ f, label }) => <button key={f} type="button" className="mm-chip" aria-pressed={s.filter === f} onClick={() => s.setFilter(f)}>{label} <small>{count(f)}</small></button>)}
        </div>
        <div className="mm-sort" role="group" aria-label="Sort makes">
          <button type="button" aria-pressed={s.sort === "new"} onClick={() => s.setSort("new")}>Newest</button>
          <button type="button" aria-pressed={s.sort === "loved"} onClick={() => s.setSort("loved")}>Most loved</button>
        </div>
      </div>

      {role === "friend" && <div className="mm-banner mm-banner-friend">
        <Heart size={20} fill="currentColor" aria-hidden="true" />
        <p><b>You’re a Thread Friend, so heart away!</b> Posting your own makes, commenting and entering challenges come with the {PLANS.member.name} plan ({PLANS.member.per}).</p>
        <JoinButton size="sm" short secondary={false} />
      </div>}
      {role === "chelsea" && <div className={`mm-banner mm-banner-chelsea${s.picking ? " is-picking" : ""}`} aria-live="polite">
        {s.picking
          ? <><Crown size={20} aria-hidden="true" /><p><b>Picking a winner.</b> Tap “Crown this one” on any October entry.</p>
              <button type="button" className="mb-btn mb-btn-ink mb-btn-sm" onClick={() => s.setPicking(false)}>Done</button></>
          : <><Star size={20} aria-hidden="true" /><p><b>Creator tools are on.</b> Use the star to feature a make, the crown for the challenge winner, the eye to hide one.{hiddenCount > 0 && ` ${hiddenCount} hidden.`}</p></>}
      </div>}

      {list.length === 0
        ? <div className="mm-empty" role="status">
            <p className="b2-note">Nothing here yet!</p>
            <p>Be the first to share one.</p>
            <ShareButton className="mb-btn mb-btn-ink" />
          </div>
        : <ul className="mm-grid" aria-label={`${list.length} makes`}>
            {list.map((m, i) => <Tile key={m.id}><Card m={m} i={i} /></Tile>)}
          </ul>}
    </div>
  </section>;
}

/** Wall order: newest first, or most hearts first. */
const arrange = (l: CMake[], sort: Sort) => [...l].sort((a, b) => sort === "loved" ? b.likes - a.likes : a.at - b.at);

function matches(m: CMake, f: Filter) {
  if (f === "all") return true;
  if (f === "challenge") return m.challenge === OCT;
  if (f === "other") return !m.projectId;
  return m.projectId === f;
}

/** Visitors: the wall, blurred, behind the club's shared locked card. */
function LockedWall() {
  const role = useRole();
  const s = useC();
  const peek = visibleTo(s.makes, role).slice(0, 8);
  return <section id="mm-wall" className="b2-sec mm-wall mm-locked" aria-labelledby="mm-wall-title">
    <div className="b2-wrap">
      <Head id="mm-wall-title" hand="show &" word="tell" tone="pink" className="mb-headsm" />
      <Locked need="friends" what="community" className="mm-locked-stage"
        reason={`Thread Friends (${PLANS.friend.per}) and Members (${PLANS.member.per}) see everything everyone’s sewing. Members also share their own makes, comment, and enter the monthly challenge.`}
        secondary={{ label: "Already in? Sign in", to: M.account }}
        peek={<ul className="mm-grid mm-grid-peek">
          {peek.map((m, i) => <li key={m.id} className="mm-peek" style={{ rotate: `${TILT[i % TILT.length]}deg` }}>
            <span className="mm-make-photo" style={{ aspectRatio: m.aspect }}><Photo m={m} /></span>
            <span className="mm-peek-line" /><span className="mm-peek-line mm-peek-short" />
          </li>)}
        </ul>} />
    </div>
  </section>;
}

/* ─── House rules ─── */

function Rules() {
  const role = useRole();
  const notes = ["Be kind. Everyone’s first zipper was wonky.", "Credit the pattern and the designer.", "Ask questions! That’s what the comments are for."];
  return <section className="b2-sec mm-rules" aria-labelledby="mm-rules-title">
    <div className="b2-wrap mm-rules-grid">
      <div>
        <Head id="mm-rules-title" hand="a few" word="house rules" tone="blue" className="mb-headsm" />
        <p className="b2-lede">Need something to make? This month we’re sewing the Patchwork Tote, live on the 2nd Tuesday and the Thursday after. Free YouTube lives most other Tuesdays.</p>
        <Link to={roleLink(M.classes, role)} className="mb-btn mb-btn-ink">See this month’s project <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
      <ul className="mm-notes">{notes.map((n, i) => <motion.li key={n} className={`mm-note mm-note-${i}`} initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: [-4, 3, -2][i] }} viewport={VIEW} transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 14 }}>{n}</motion.li>)}</ul>
    </div>
  </section>;
}

/* ─── Lightbox ─── */

function CommentItem({ x, reply = false }: { x: Comment; reply?: boolean }) {
  return <li className={`mm-c${reply ? " mm-c-reply" : ""}`}>
    <Avatar m={x.by} size={reply ? 26 : 32} />
    <div className="mm-c-body">
      <p className="mm-c-meta"><b>{x.by.name}</b>{x.by.badge === "chelsea" && <span className="mm-c-host">host</span>}<span>{x.ago === "now" ? "just now" : x.ago}</span></p>
      <p className="mm-c-text">{x.text}</p>
      {x.replies && <ul className="mm-c-list">{x.replies.map(r => <CommentItem key={r.id} x={r} reply />)}</ul>}
    </div>
  </li>;
}

function Lightbox() {
  const role = useRole();
  const s = useC();
  const shown = arrange(visibleTo(s.makes, role).filter(m => matches(m, s.filter)), s.sort);
  const pool = shown.some(m => m.id === s.open) ? shown : arrange(visibleTo(s.makes, role), s.sort);
  const idx = pool.findIndex(m => m.id === s.open);
  const m = idx >= 0 ? pool[idx] : undefined;
  const close = () => s.setOpen(null);
  const ref = useDialog<HTMLDivElement>(!!m, close);
  const [draft, setDraft] = useState("");
  const list = useRef<HTMLUListElement>(null);
  const titleId = useId();
  const go = (d: number) => { if (pool.length) s.setOpen(pool[(idx + d + pool.length) % pool.length].id); setDraft(""); };
  useEffect(() => {
    if (!m) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea")) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  const canComment = role === "member" || role === "chelsea";
  const send = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || !m) return;
    s.comment(m.id, me(role), t);
    setDraft("");
    requestAnimationFrame(() => list.current?.lastElementChild?.scrollIntoView({ block: "nearest" }));
  };
  const project = m && PROJECTS.find(p => p.id === m.projectId);
  return <AnimatePresence>
    {m && <motion.div className="mm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <motion.div ref={ref} className="mm-box" role="dialog" aria-modal="true" aria-labelledby={titleId}
        initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.3, ease: EASE }}>
        <button type="button" className="mm-x" onClick={close} aria-label="Close" data-autofocus><X size={22} aria-hidden="true" /></button>
        <div className="mm-box-photo">
          <div className="mm-box-frame">
            <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
            <span className="mm-box-img" style={{ aspectRatio: m.aspect }}>
              <Photo m={m} sizes="big" />
              {m.hidden && <span className="mm-hidden-veil"><EyeOff size={18} aria-hidden="true" /> Hidden from the club</span>}
            </span>
          </div>
          {pool.length > 1 && <div className="mm-box-nav">
            <button type="button" onClick={() => go(-1)} aria-label="Previous make"><ChevronLeft size={22} aria-hidden="true" /></button>
            <span aria-live="polite">{idx + 1} of {pool.length}</span>
            <button type="button" onClick={() => go(1)} aria-label="Next make"><ChevronRight size={22} aria-hidden="true" /></button>
          </div>}
        </div>
        <div className="mm-box-side">
          <p className="mm-box-tags">
            {project && <Link to={roleLink(M.classes, role)} className="mm-tag mm-tag-link">{project.title}</Link>}
            {m.challenge && <span className={`mm-tag mm-tag-chal${m.challenge === OCT ? "" : " is-past"}`}><Trophy size={11} aria-hidden="true" /> {m.challenge}</span>}
            {s.featured === m.id && <span className="mm-tag mm-tag-pick"><Star size={11} fill="currentColor" aria-hidden="true" /> Chelsea’s pick</span>}
            {(s.winner === m.id || m.id === LAST_WINNER) && <span className="mm-tag mm-tag-pick"><Crown size={11} aria-hidden="true" /> Winner</span>}
          </p>
          <h2 id={titleId} className="mm-box-title">{m.title}</h2>
          <p className="mm-box-by"><Avatar m={m.by} size={38} /><span className="mm-box-who"><b>{m.by.name}</b>{m.by.badge === "founding" && <span className="mm-founding">{FOUNDING}</span>}<small>{m.ago}{m.by.since && ` · member since ${m.by.since}`}</small></span></p>
          {m.story && <p className="mm-box-story">{m.story}</p>}
          <div className="mm-box-actions">
            <LikeButton m={m} big />
            {role === "chelsea" && <Tools m={m} labels />}
          </div>
          <h3 className="mm-box-h">Comments <span>{countComments(m, role === "chelsea")}</span></h3>
          {m.comments.length === 0 && <p className="mm-box-none">No comments yet. Say something nice!</p>}
          <ul ref={list} className="mm-c-list" aria-live="polite">{m.comments.map(x => <CommentItem key={x.id} x={x} />)}</ul>
          {canComment
            ? <form className="mm-c-form" onSubmit={send}>
                <Avatar m={me(role)} size={32} />
                <label htmlFor={`${titleId}-c`} className="ma-sr">Add a comment</label>
                <input id={`${titleId}-c`} value={draft} onChange={e => setDraft(e.target.value)} placeholder={`Cheer on ${m.by.id === me(role).id ? "yourself" : m.by.name.split(" ")[0]}…`} maxLength={400} autoComplete="off" />
                <button type="submit" disabled={!draft.trim()} aria-label="Post comment"><Send size={17} aria-hidden="true" /></button>
              </form>
            : <p className="mb-lockrow mm-c-locked"><Lock size={14} aria-hidden="true" /> <span>Commenting is for Members.</span>
                <Link to={roleLink(M.join, role)}>{joinCta(role, "members")?.short ?? "Join the club"} <ArrowRight size={13} aria-hidden="true" /></Link></p>}
        </div>
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}

/* ─── Share form ─── */

function ShareModal() {
  const role = useRole();
  const s = useC();
  const open = !!s.sharing;
  const close = () => s.setSharing(false);
  const ref = useDialog<HTMLFormElement>(open, close);
  const [file, setFile] = useState<{ url: string; name: string } | null>(null);
  const [title, setTitle] = useState("");
  const [project, setProject] = useState("tote");
  const [story, setStory] = useState("");
  const [enter, setEnter] = useState(false);
  const [err, setErr] = useState("");
  const [swatch, setSwatch] = useState(0);
  const fileUrl = useRef<string | null>(null);
  const kept = useRef(false);
  const uid = useId();

  // Fresh form each time it opens; drop an unused photo when it closes.
  useEffect(() => {
    if (open) {
      setFile(null); setTitle(""); setProject("tote"); setStory(""); setErr(""); kept.current = false;
      setEnter(s.sharing ? s.sharing.challenge : false); setSwatch(Math.floor(Math.random() * 4));
    } else if (fileUrl.current && !kept.current) { URL.revokeObjectURL(fileUrl.current); fileUrl.current = null; }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickFile = (f?: File) => {
    if (!f || !f.type.startsWith("image/")) return;
    if (fileUrl.current) URL.revokeObjectURL(fileUrl.current);
    const url = URL.createObjectURL(f);
    fileUrl.current = url;
    setFile({ url, name: f.name });
  };
  const removeFile = () => { if (fileUrl.current) URL.revokeObjectURL(fileUrl.current); fileUrl.current = null; setFile(null); };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setErr("Give your make a name (anything works!)"); document.getElementById(`${uid}-title`)?.focus(); return; }
    kept.current = true;
    s.add({ id: `u${Date.now()}`, by: me(role), title: title.trim(), image: file?.url ?? "", swatch, aspect: file ? "4/5" : "1/1",
      projectId: project === "none" ? undefined : project, challenge: enter ? OCT : undefined, likes: 0, at: -Date.now(), ago: "Just now",
      story: story.trim() || undefined, comments: [], mine: true }, file?.url);
    fileUrl.current = null;
    close();
  };
  return <AnimatePresence>
    {open && <motion.div className="mm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <motion.form ref={ref} className="mm-share" role="dialog" aria-modal="true" aria-labelledby={`${uid}-h`} onSubmit={submit} noValidate
        initial={{ opacity: 0, y: 30, rotate: -1 }} animate={{ opacity: 1, y: 0, rotate: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.3, ease: EASE }}>
        <button type="button" className="mm-x" onClick={close} aria-label="Close"><X size={22} aria-hidden="true" /></button>
        <p className="mm-share-hand" aria-hidden="true">ooh, what’d you make?</p>
        <h2 id={`${uid}-h`} className="mm-share-title">Share your make</h2>

        <div className="mm-share-grid">
          <div className="mm-drop-wrap">
            {file
              ? <figure className="mm-drop mm-drop-full">
                  <img src={file.url} alt={`Preview of ${file.name}`} />
                  <figcaption>
                    <label className="mm-mini" htmlFor={`${uid}-file`}>Change</label>
                    <button type="button" className="mm-mini" onClick={removeFile}>Remove</button>
                  </figcaption>
                </figure>
              : <label className="mm-drop" htmlFor={`${uid}-file`}
                  onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pickFile(e.dataTransfer.files[0]); }}>
                  <span className={`mm-swatch mm-swatch-${swatch}`} aria-hidden="true" />
                  <span className="mm-drop-in"><ImagePlus size={28} aria-hidden="true" /><b>Add a photo</b><small>Tap to choose, or drop it here</small></span>
                </label>}
            <input id={`${uid}-file`} className="mm-file" type="file" accept="image/*" onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
            {!file && <p className="mm-hint">No photo handy? We’ll pin a fabric swatch for now.</p>}
          </div>

          <div className="mm-fields">
            <div className="mm-field">
              <label htmlFor={`${uid}-title`}>What is it?</label>
              <input id={`${uid}-title`} value={title} maxLength={60} placeholder="My scrappy tote" autoComplete="off" data-autofocus
                aria-invalid={!!err} aria-describedby={err ? `${uid}-err` : undefined} onChange={e => { setTitle(e.target.value); if (err) setErr(""); }} />
              {err && <p id={`${uid}-err`} className="mm-err">{err}</p>}
            </div>
            <fieldset className="mm-field mm-projects">
              <legend>Which project?</legend>
              {[...PROJECTS.map(p => ({ id: p.id, label: p.title })), { id: "none", label: "Something else" }].map(p =>
                <label key={p.id} className={`mm-radio${project === p.id ? " is-on" : ""}`}>
                  <input type="radio" name={`${uid}-p`} value={p.id} checked={project === p.id} onChange={() => setProject(p.id)} /><span>{p.label}</span>
                </label>)}
            </fieldset>
            <div className="mm-field">
              <label htmlFor={`${uid}-story`}>The story <small>(optional)</small></label>
              <textarea id={`${uid}-story`} rows={3} value={story} maxLength={280} placeholder="Fabric, pattern, what went wrong, what went right…" aria-describedby={`${uid}-count`} onChange={e => setStory(e.target.value)} />
              <p id={`${uid}-count`} className="mm-count">{280 - story.length} characters left</p>
            </div>
            <label className={`mm-enter${enter ? " is-on" : ""}`}>
              <input type="checkbox" checked={enter} onChange={e => setEnter(e.target.checked)} />
              <span className="mm-enter-box" aria-hidden="true"><Trophy size={16} /></span>
              <span><b>Enter {OCT}</b><small>Scraps only · closes {CHALLENGE.ends} · win a fat quarter bundle</small></span>
            </label>
          </div>
        </div>

        <div className="mm-share-actions">
          <button type="button" className="mb-btn mb-btn-line" onClick={close}>Cancel</button>
          <button type="submit" className="mb-btn mb-btn-ink"><Send size={16} aria-hidden="true" /> Post my make</button>
        </div>
      </motion.form>
    </motion.div>}
  </AnimatePresence>;
}

/** Thread Friends who try to post: the club's locked card (same pieces as <Locked>), in a dialog. */
function Upsell() {
  const s = useC();
  const close = () => s.setUpsell(false);
  const ref = useDialog<HTMLDivElement>(s.upsell, close);
  return <AnimatePresence>
    {s.upsell && <motion.div className="mm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <motion.div ref={ref} className="mb-locked-card mm-upsell" role="dialog" aria-modal="true" aria-labelledby="mm-up-h" aria-describedby="mm-up-p"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ type: "spring", stiffness: 160, damping: 18 }}>
        <button type="button" className="mm-x" onClick={close} aria-label="Close"><X size={20} aria-hidden="true" /></button>
        <span className="mb-locked-icon"><Lock size={22} aria-hidden="true" /></span>
        <p id="mm-up-h" className="mb-locked-hand">sharing makes is for members</p>
        <p id="mm-up-p" className="mb-locked-text">As a {PLANS.friend.name} ({PLANS.friend.per}) you can browse and heart every make. {PLANS.member.name}s ({PLANS.member.per}) post their own, comment, and enter the monthly challenge, plus two live classes a month and every replay.</p>
        <div className="mb-locked-actions">
          <JoinButton need="members" />
          <button type="button" className="mb-locked-link mm-linkbtn" onClick={close} data-autofocus>Keep browsing</button>
        </div>
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}

/* ─── Page ─── */

export function MembersCommunity() {
  const state = useCommunityState();
  return <Ctx.Provider value={state}>
    <MembersPage page="community" header={<Header />}>
      <Body />
      {state.toast.node}
    </MembersPage>
  </Ctx.Provider>;
}

function Body() {
  const role = useRole();
  const open = can(role, "community");
  return <>
    <Part p="club"><Challenge /></Part>
    <Part p="faves">{open ? <Wall /> : <LockedWall />}</Part>
    <Part p="loop"><Rules /></Part>
    {open && <><Lightbox /><ShareModal /><Upsell /></>}
  </>;
}
